import { useState } from 'react';

import type { Relation } from '@lifetimeline/shared';

import { Button } from '../../components/Button.js';
import { useToast } from '../../components/Toast.js';
import { useI18n } from '../../i18n/index.js';
import { api, errorMessageKey } from '../../lib/apiClient.js';

interface ConnectButtonProps {
  handle: string;
  relation: Relation;
  /** 바뀌고 나면 프로필을 다시 읽는다 — 관계에 따라 보이는 것이 통째로 달라진다 */
  onChanged: () => void;
}

/**
 * 관계에 따라 **다른 일을 하는 한 자리.**
 *
 *   none       신청한다
 *   outgoing   내가 보낸 신청을 물린다
 *   incoming   저쪽이 보낸 신청을 받는다 (거절도 여기서)
 *   connected  끊는다
 *
 * 상태마다 버튼을 따로 두면 화면에 네 개가 생기고 그중 셋은 늘 의미가 없다.
 */
export function ConnectButton({ handle, relation, onChanged }: ConnectButtonProps) {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (relation === 'self') {
    return null;
  }

  const run = async (call: () => Promise<unknown>, done: Parameters<typeof t>[0]) => {
    setBusy(true);
    try {
      await call();
      toast.show(t(done));
      onChanged();
    } catch (cause) {
      toast.show(t(errorMessageKey(cause)), 'error');
    } finally {
      setBusy(false);
    }
  };

  const path = `/api/connections/${encodeURIComponent(handle)}`;

  /*
   * 받은 신청에만 버튼이 둘이다. 수락과 거절은 **서로 다른 결과**라 한
   * 자리에 담을 수 없다 — 나머지 상태는 되돌리는 동작 하나뿐이다.
   */
  if (relation === 'incoming') {
    return (
      <>
        <Button
          loading={busy}
          onClick={() => void run(() => api.post(`${path}/accept`, {}), 'connection.accepted')}
        >
          {t('connection.accept')}
        </Button>
        {/* 거절은 되돌릴 수 없다 — 수락 옆에서 색으로 구분되어야 헛누르지 않는다 */}
        <Button
          variant="danger"
          loading={busy}
          onClick={() => void run(() => api.delete(path), 'connection.rejected')}
        >
          {t('connection.reject')}
        </Button>
      </>
    );
  }

  if (relation === 'outgoing') {
    return (
      <Button
        variant="outline"
        loading={busy}
        onClick={() => void run(() => api.delete(path), 'connection.canceled')}
      >
        {t('connection.waiting')}
      </Button>
    );
  }

  if (relation === 'connected') {
    return (
      <Button
        variant="outline"
        loading={busy}
        onClick={() => void run(() => api.delete(path), 'connection.removed')}
      >
        {t('connection.connected')}
      </Button>
    );
  }

  return (
    <Button
      loading={busy}
      onClick={() => void run(() => api.post(path, {}), 'connection.requestSent')}
    >
      {t('connection.request')}
    </Button>
  );
}
